import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
} from "@angular/forms";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { MatMenuModule } from "@angular/material/menu";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatInputModule } from "@angular/material/input";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { provideNativeDateAdapter } from "@angular/material/core";
import { MatTooltipModule } from "@angular/material/tooltip";
import { ReusablePaginatorComponent } from "../reusable-paginator/reusable-paginator.component";
import { ApprovalStatus, PriceGroupRecord } from "./price-group.model";
import { PriceGroupDialogComponent } from "./price-group-dialog.component";

import { Subscription } from "rxjs";
import { ToastService } from "../../_services/toast.service";
import { PriceGroupsService, priceGroupRecord } from "./price-groups.service";

export function localToday(): Date {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
}
export function validDate(date: unknown): date is Date {
  return date instanceof Date && !Number.isNaN(date.getTime());
}
export function priceGroupDateRange(
  control: AbstractControl,
): ValidationErrors | null {
  const { startDate, endDate } = control.value;
  if (startDate && !endDate) return { endRequired: true };
  if (endDate && !startDate) return { startRequired: true };
  if ((startDate && !validDate(startDate)) || (endDate && !validDate(endDate)))
    return { invalidDate: true };
  if (
    (startDate && startDate > localToday()) ||
    (endDate && endDate > localToday())
  )
    return { futureDate: true };
  if (startDate && endDate && startDate > endDate) return { dateOrder: true };
  return null;
}

@Component({
  selector: "app-price-groups",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatMenuModule,
    MatDatepickerModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    MatTooltipModule,
    ReusablePaginatorComponent,
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: "./price-groups.component.html",
  styleUrls: ["./price-groups.component.scss"],
})
export class PriceGroupsComponent implements OnInit, OnDestroy {
  @ViewChild("filterDropdown") filterDropdown?: ElementRef<HTMLElement>;
  records: PriceGroupRecord[] = [];
  totalCount = 0;
  loading = false;
  readonly skeletonRows = Array.from({ length: 5 }, (_, index) => index);
  readonly skeletonColumns = Array.from({ length: 11 }, (_, index) => index);
  error = "";
  private listSubscription?: Subscription;
  private subscriptions = new Subscription();
  currentSupplierId: number | string | null = null;
  currentPage = 1;
  pageSize = 10;
  filtersOpen = false;
  announcement = "";
  readonly filterForm = new FormGroup(
    {
      search: new FormControl("", { nonNullable: true }),
      approvalStatus: new FormControl<"" | ApprovalStatus>("", {
        nonNullable: true,
      }),
      status: new FormControl<"" | "enabled" | "disabled">("", {
        nonNullable: true,
      }),
      startDate: new FormControl<Date | null>(null),
      endDate: new FormControl<Date | null>(null),
    },
    { validators: priceGroupDateRange },
  );
  appliedFilters = this.filterForm.getRawValue();
  get today(): Date {
    return localToday();
  }
  get startDateMax(): Date {
    const end = this.filterForm.controls.endDate.value;
    return validDate(end) && end < this.today ? end : this.today;
  }
  get endDateMin(): Date | null {
    return this.filterForm.controls.startDate.value;
  }
  get hasFilters(): boolean {
    const f = this.appliedFilters;
    return !!(
      f.search ||
      f.status ||
      f.approvalStatus ||
      f.startDate ||
      f.endDate
    );
  }
  constructor(
    private dialog: MatDialog,
    private priceGroups: PriceGroupsService,
    private toast: ToastService,
  ) {}

  ngOnInit(): void {
    this.loadRecords();
    this.subscriptions.add(
      this.priceGroups.currentSupplierId().subscribe({
        next: (id) => {
          this.currentSupplierId = id;
        },
        error: () => {
          this.currentSupplierId = null;
          this.toast.displayToast(
            "Unable to verify your supplier account. Editing is unavailable. Please reload to try again.",
            "right",
            "danger",
          );
        },
      }),
    );
  }
  ngOnDestroy(): void {
    this.listSubscription?.unsubscribe();
    this.subscriptions.unsubscribe();
  }

  loadRecords(): void {
    this.listSubscription?.unsubscribe();
    this.loading = true;
    this.error = "";
    this.records = [];
    this.totalCount = 0;
    const filters = this.appliedFilters;
    const params: Record<string, string> = {
      page: String(this.currentPage),
      limit: String(this.pageSize),
    };
    if (filters.search) params.search = filters.search;
    if (filters.status) params.status = String(filters.status === "enabled");
    if (filters.approvalStatus) params.approvalStatus = filters.approvalStatus;
    if (filters.startDate && filters.endDate) {
      params.from_date = this.apiDate(filters.startDate);
      params.to_date = this.apiDate(filters.endDate);
    }
    this.listSubscription = this.priceGroups.list(params).subscribe({
      next: (listing) => {
        this.records = listing.result
          .filter((row) => row.isSupplierRequest === true)
          .map(priceGroupRecord);
        this.totalCount = Number(listing.total_count);
        this.loading = false;
      },
      error: () => {
        this.error = "Unable to load price groups. Please try again.";
        this.loading = false;
        this.toast.displayToast(this.error, "right", "danger");
      },
    });
  }

  private apiDate(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  }
  openFilters(): void {
    if (!this.filtersOpen) {
      this.filterForm.reset(this.appliedFilters);
      this.filtersOpen = true;
    }
  }
  @HostListener("document:pointerdown", ["$event"])
  closeFiltersOutside(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    // The datepicker is rendered in an overlay outside the dropdown.
    if (
      this.filtersOpen &&
      !this.filterDropdown?.nativeElement.contains(target) &&
      !target.closest?.(".mat-datepicker-content, .price-group-status-panel")
    )
      this.filtersOpen = false;
  }
  applyFilters(): void {
    this.filterForm.markAllAsTouched();
    if (this.filterForm.invalid) {
      this.toast.displayToast(
        "Choose a valid date range with both a start and end date, on or before today.",
        "right",
        "warning",
      );
      return;
    }
    this.appliedFilters = {
      ...this.filterForm.getRawValue(),
      search: this.filterForm.controls.search.value.trim(),
    };
    this.currentPage = 1;
    this.filtersOpen = false;
    this.loadRecords();
  }
  clearFilters(): void {
    this.filterForm.reset({
      search: "",
      status: "",
      approvalStatus: "",
      startDate: null,
      endDate: null,
    });
    this.appliedFilters = this.filterForm.getRawValue();
    this.currentPage = 1;
    this.filtersOpen = false;
    this.loadRecords();
  }
  changePage(page: number): void {
    if (page === this.currentPage) return;
    this.currentPage = page;
    this.loadRecords();
  }
  changePageSize(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.loadRecords();
  }

  canEdit(record: PriceGroupRecord): boolean {
    return (
      record.isSupplierRequest === true &&
      record.supplier_id != null &&
      this.currentSupplierId != null &&
      String(record.supplier_id) === String(this.currentSupplierId)
    );
  }

  openEditor(mode: "add" | "view" | "edit", record?: PriceGroupRecord): void {
    if (mode !== "add" && !record) return;
    if (mode === "edit" && (!record || !this.canEdit(record))) {
      this.toast.displayToast(
        "You can only edit your own supplier requests.",
        "right",
        "warning",
      );
      return;
    }
    if (mode !== "add" && record) {
      this.subscriptions.add(
        this.priceGroups.details(record.id).subscribe({
          next: (details) => {
            if (mode === "edit" && !this.canEdit(details)) {
              this.error = "This price group is unavailable for editing.";
              this.toast.displayToast(this.error, "right", "warning");
              return;
            }
            this.showEditor(mode, details);
          },
          error: () => {
            this.error =
              "Unable to load price group details. Please try again.";
            this.toast.displayToast(this.error, "right", "danger");
          },
        }),
      );
      return;
    }
    this.showEditor("add");
  }

  private showEditor(
    mode: "add" | "view" | "edit",
    record?: PriceGroupRecord,
  ): void {
    this.subscriptions.add(
      this.dialog
        .open(PriceGroupDialogComponent, {
          width: "680px",
          maxWidth: "calc(100vw - 32px)",
          maxHeight: "90vh",
          autoFocus: "first-tabbable",
          data: { mode, record },
        })
        .afterClosed()
        .subscribe((created?: boolean) => {
          if (!created || mode === "view") return;
          this.currentPage = 1;
          this.announcement =
            mode === "edit"
              ? "Price group updated and resubmitted for approval."
              : "Price group submitted for approval.";
          this.toast.displayToast(this.announcement, "right", "success");
          this.loadRecords();
        }),
    );
  }
}
